import React from 'react';
import { motion } from 'framer-motion';
import { FaArrowRight, FaCheck, FaCloud, FaCogs, FaExchangeAlt, FaBolt, FaProjectDiagram, FaShieldAlt, FaChartBar } from 'react-icons/fa';
import { Button } from '@/components/ui/button';
import { Link } from 'wouter';

const FeatureCard = ({
    icon: Icon,
    title,
    description
}: {
    icon: React.ComponentType<any>;
    title: string;
    description: string;
}) => (
    <motion.div
        className="bg-white rounded-xl p-8 shadow-md border border-gray-100 h-full hover:shadow-xl hover:border-violet-100 transition-all duration-300 group"
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.5 }}
    >
        <div className="w-14 h-14 bg-violet-50 rounded-lg flex items-center justify-center mb-6 text-violet-600 group-hover:bg-violet-600 group-hover:text-white transition-colors duration-300">
            <Icon className="w-6 h-6" />
        </div>
        <h4 className="text-xl font-semibold mb-3 text-gray-900">{title}</h4>
        <p className="text-gray-600">{description}</p>
    </motion.div>
);

const MulesoftToWorkato = () => {
    return (
        <div className="min-h-screen">
            <div className="bg-amber-600 text-white px-4 py-3 text-center relative z-50">
                <p className="text-sm font-medium flex justify-center items-center gap-2">
                    <span>🚧</span>
                    <span><strong>Coming Soon:</strong> This migration accelerator is currently under active development.</span>
                </p>
            </div>
            {/* Hero Section */}
            <section className="py-16 lg:py-24 relative overflow-hidden bg-slate-900">
                <div className="absolute inset-0 bg-gradient-to-br from-violet-950 via-slate-900 to-slate-950 z-0"></div>
                <div className="absolute -right-[300px] top-0 w-[600px] h-[600px] bg-violet-500/10 rounded-full blur-3xl"></div>

                <div className="container mx-auto px-4 lg:px-8 relative z-10">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-center">
                        <div className="text-left lg:pr-4">
                            <div className="flex items-center mb-4">
                                <span className="inline-block text-xs font-semibold bg-violet-500/20 text-violet-400 px-3 py-1 rounded-full">
                                    MULESOFT MIGRATION
                                </span>
                            </div>

                            <motion.h1
                                className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6"
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.6 }}
                            >
                                Mulesoft to Workato Migration
                            </motion.h1>

                            <motion.p
                                className="text-lg mb-8 text-gray-300"
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.6, delay: 0.2 }}
                            >
                                Modernize your integration stack by moving to Workato's leading AI-powered automation platform. Accelerate delivery, reduce costs, and empower your business teams.
                            </motion.p>

                            <motion.div
                                className="flex flex-col sm:flex-row gap-4 mb-8"
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.6, delay: 0.3 }}
                            >
                                <Button className="h-12 px-6 text-sm font-medium rounded-md bg-violet-600 hover:bg-violet-700 group" asChild>
                                    <Link href="/request-demo">
                                        <span>Start Migration</span>
                                        <FaArrowRight className="ml-2 h-3.5 w-3.5 group-hover:translate-x-1 transition-transform" />
                                    </Link>
                                </Button>

                                <Button className="h-12 px-6 text-sm font-medium rounded-md bg-white text-violet-900 hover:bg-violet-50 border border-transparent group" asChild>
                                    <Link href="/mule-to-springboot-converter-api-analyzer">
                                        <FaChartBar className="mr-2 h-4 w-4 text-violet-600" />
                                        <span>Free Assessment</span>
                                    </Link>
                                </Button>
                            </motion.div>

                            <motion.div
                                className="space-y-4"
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                transition={{ duration: 0.8, delay: 0.5 }}
                            >
                                {[
                                    "Accelerated integration delivery with Low-Code/No-Code",
                                    "Significant reduction in TCO and licensing costs",
                                    "Democratized integration for business users"
                                ].map((item, index) => (
                                    <div key={index} className="flex items-center gap-2">
                                        <div className="text-green-400">
                                            <FaCheck className="w-5 h-5" />
                                        </div>
                                        <span className="text-gray-300">{item}</span>
                                    </div>
                                ))}
                            </motion.div>
                        </div>

                        <motion.div
                            className="relative"
                            initial={{ opacity: 0, x: 20 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ duration: 0.8, delay: 0.3 }}
                        >
                            <div className="relative bg-gradient-to-br from-violet-900/30 to-slate-900/40 rounded-xl p-6 shadow-xl border border-violet-900/20 overflow-hidden min-h-[400px] flex items-center justify-center">
                                <div className="absolute inset-0 bg-gradient-to-tr from-violet-500/5 via-fuchsia-500/5 to-purple-500/5 z-0"></div>

                                {/* Visual Container */}
                                <div className="relative z-10 w-full flex flex-col md:flex-row items-center justify-between gap-6 px-4">
                                    {/* Left Side: Legacy (Complexity) */}
                                    <div className="bg-white/95 p-5 rounded-xl border border-gray-200 shadow-lg w-full md:w-5/12 relative overflow-hidden">
                                        <div className="absolute top-0 left-0 w-full h-1 bg-gray-400"></div>
                                        <h5 className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-4 text-center">Legacy Mulesoft</h5>

                                        {/* Abstract Spaghetti Code Visual */}
                                        <div className="relative h-40 w-full opacity-60">
                                            <svg width="100%" height="100%" viewBox="0 0 100 100" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                <circle cx="20" cy="20" r="4" fill="#6B7280" />
                                                <circle cx="80" cy="30" r="4" fill="#6B7280" />
                                                <circle cx="40" cy="70" r="4" fill="#6B7280" />
                                                <circle cx="70" cy="80" r="4" fill="#6B7280" />
                                                <circle cx="50" cy="50" r="4" fill="#6B7280" />
                                                <path d="M20 20 L80 30" stroke="#9CA3AF" strokeWidth="1" />
                                                <path d="M20 20 L50 50" stroke="#9CA3AF" strokeWidth="1" />
                                                <path d="M50 50 L40 70" stroke="#9CA3AF" strokeWidth="1" />
                                                <path d="M80 30 L70 80" stroke="#9CA3AF" strokeWidth="1" />
                                                <path d="M40 70 L70 80" stroke="#9CA3AF" strokeWidth="1" />
                                                <path d="M20 20 L40 70" stroke="#9CA3AF" strokeWidth="1" />
                                            </svg>
                                            <div className="absolute inset-0 flex items-center justify-center">
                                                <div className="bg-gray-100/90 px-3 py-1 rounded text-[10px] sm:text-xs font-mono text-gray-600 border border-gray-300">
                                                    &lt;xml:config...&gt;
                                                </div>
                                            </div>
                                        </div>
                                        <div className="text-center mt-2">
                                            <span className="text-xs text-red-500 font-medium flex items-center justify-center gap-1">
                                                <FaCogs className="w-3 h-3" /> Complex Code
                                            </span>
                                        </div>
                                    </div>

                                    {/* Middle: Transformation */}
                                    <div className="flex flex-col items-center justify-center z-10">
                                        <div className="w-10 h-10 bg-violet-600 rounded-full flex items-center justify-center shadow-lg shadow-violet-500/30 mb-2">
                                            <FaBolt className="text-white w-5 h-5" />
                                        </div>
                                        <p className="text-[10px] text-violet-300 font-medium uppercase tracking-widest bg-slate-900/50 px-2 py-0.5 rounded">AI Migration</p>
                                    </div>

                                    {/* Right Side: Workato (Recipe) */}
                                    <div className="bg-white/95 p-5 rounded-xl border border-violet-200 shadow-xl shadow-violet-900/10 w-full md:w-5/12 relative">
                                        <div className="absolute top-0 left-0 w-full h-1 bg-violet-500"></div>
                                        <h5 className="text-xs font-bold text-violet-600 uppercase tracking-wider mb-4 text-center">Modern Workato Recipe</h5>

                                        {/* Recipe Visual */}
                                        <div className="flex flex-col gap-3 relative">
                                            {/* Connector Line */}
                                            <div className="absolute left-4 top-4 bottom-4 w-0.5 bg-gray-200"></div>

                                            {/* Trigger */}
                                            <div className="relative flex items-center gap-3 bg-violet-50 p-2 rounded-lg border border-violet-100 z-10">
                                                <div className="w-8 h-8 rounded bg-white flex items-center justify-center border border-gray-100 shadow-sm shrink-0">
                                                    <span className="text-orange-500 font-bold text-xs">SF</span>
                                                </div>
                                                <div className="flex flex-col">
                                                    <span className="text-[10px] font-bold text-gray-700">New Opportunity</span>
                                                    <span className="text-[9px] text-gray-500">Salesforce • Real-time</span>
                                                </div>
                                            </div>

                                            {/* Action 1 */}
                                            <div className="relative flex items-center gap-3 bg-white p-2 rounded-lg border border-gray-100 z-10 ml-4 shadow-sm">
                                                <div className="w-7 h-7 rounded bg-blue-50 flex items-center justify-center border border-blue-100 shrink-0">
                                                    <span className="text-blue-600 font-bold text-[10px]">AI</span>
                                                </div>
                                                <div className="flex flex-col">
                                                    <span className="text-[10px] font-bold text-gray-700">Enrich Data</span>
                                                    <span className="text-[9px] text-gray-500">Workato Copilot</span>
                                                </div>
                                            </div>

                                            {/* Action 2 */}
                                            <div className="relative flex items-center gap-3 bg-white p-2 rounded-lg border border-gray-100 z-10 ml-4 shadow-sm">
                                                <div className="w-7 h-7 rounded bg-green-50 flex items-center justify-center border border-green-100 shrink-0">
                                                    <span className="text-green-600 font-bold text-[10px]">SL</span>
                                                </div>
                                                <div className="flex flex-col">
                                                    <span className="text-[10px] font-bold text-gray-700">Notify Sales</span>
                                                    <span className="text-[9px] text-gray-500">Slack</span>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div className="absolute bottom-4 left-0 right-0 text-center">
                                    <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-violet-500/10 border border-violet-500/20 text-violet-200 text-xs">
                                        <span className="w-2 h-2 rounded-full bg-green-400 animate-pulse"></span>
                                        Live Migration Preview
                                    </div>
                                </div>
                            </div>
                        </motion.div>
                    </div>
                </div>
            </section>

            {/* Key Benefits */}
            <section className="py-20 bg-white">
                <div className="container mx-auto px-4 lg:px-8">
                    <div className="max-w-3xl mx-auto text-center mb-16">
                        <h2 className="text-sm font-semibold text-violet-600 uppercase tracking-wider mb-3">KEY BENEFITS</h2>
                        <h3 className="text-3xl md:text-4xl font-bold mb-6 tracking-tight text-gray-900">Why Migrate to Workato?</h3>
                        <p className="text-lg text-gray-600">
                            Transform your business with the leader in Enterprise Automation
                        </p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                        <FeatureCard
                            icon={FaBolt}
                            title="Speed to Value"
                            description="Build and deploy integrations 10x faster with Workato's low-code/no-code platform compared to traditional ESBs."
                        />
                        <FeatureCard
                            icon={FaCloud}
                            title="Cloud Native"
                            description="True cloud-native architecture that scales automatically without the maintenance overhead of on-premise infrastructure."
                        />
                        <FeatureCard
                            icon={FaProjectDiagram}
                            title="Democratized Integration"
                            description="Empower business users (citizen integrators) to build their own automations while IT maintains governance."
                        />
                        <FeatureCard
                            icon={FaShieldAlt}
                            title="Enterprise Security"
                            description="Bank-grade security and governance features ensure your data is always protected and compliant."
                        />
                        <FeatureCard
                            icon={FaCogs}
                            title="AI-Powered Automation"
                            description="Leverage Workato's AI features (Workato Copilot) to build complex workflows using natural language."
                        />
                        <FeatureCard
                            icon={FaExchangeAlt}
                            title="Seamless Connectivity"
                            description="Pre-built connectors for thousands of SaaS applications eliminate the need for custom coding."
                        />
                    </div>
                </div>
            </section>

            {/* Comparison Table */}
            <section className="py-20 bg-gray-50">
                <div className="container mx-auto px-4 lg:px-8">
                    <div className="max-w-3xl mx-auto text-center mb-16">
                        <h2 className="text-sm font-semibold text-violet-600 uppercase tracking-wider mb-3">COMPARISON</h2>
                        <h3 className="text-3xl md:text-4xl font-bold mb-6 tracking-tight text-gray-900">Mulesoft vs. Workato</h3>
                        <p className="text-lg text-gray-600">
                            See the clear advantages of modernizing with Workato
                        </p>
                    </div>

                    <div className="max-w-4xl mx-auto overflow-hidden rounded-xl shadow-lg border border-gray-200 bg-white">
                        <table className="w-full">
                            <thead>
                                <tr>
                                    <th className="p-4 text-left bg-gray-50 text-gray-600 font-semibold border-b border-gray-200">Feature</th>
                                    <th className="p-4 text-center bg-violet-50 text-violet-700 font-semibold border-b border-gray-200">
                                        <div className="flex items-center justify-center">
                                            <span>Workato</span>
                                        </div>
                                    </th>
                                    <th className="p-4 text-center bg-gray-50 text-gray-600 font-semibold border-b border-gray-200">
                                        <div className="flex items-center justify-center">
                                            <span>Mulesoft</span>
                                        </div>
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                {[
                                    {
                                        feature: "Development Approach",
                                        workato: "Low-Code / No-Code (Faster)",
                                        mulesoft: "Heavy Code / XML (Slower)"
                                    },
                                    {
                                        feature: "Maintenance",
                                        workato: "Zero-Ops, Fully Managed",
                                        mulesoft: "High Maintenance (Updates, Patches)"
                                    },
                                    {
                                        feature: "User Base",
                                        workato: "IT & Business Teams",
                                        mulesoft: "Specialized Developers Only"
                                    },
                                    {
                                        feature: "Time to Market",
                                        workato: "Weeks / Days",
                                        mulesoft: "Months"
                                    },
                                    {
                                        feature: "AI Capabilities",
                                        workato: "Native AI Copilot",
                                        mulesoft: "Limited / Add-on"
                                    },
                                    {
                                        feature: "TCO",
                                        workato: "Significantly Lower",
                                        mulesoft: "High (Licensing + Resources)"
                                    }
                                ].map((row, index) => (
                                    <tr key={index} className={index % 2 === 0 ? "bg-white" : "bg-gray-50"}>
                                        <td className="p-4 text-left font-medium text-gray-900 border-b border-gray-200">{row.feature}</td>
                                        <td className="p-4 text-center text-gray-800 border-b border-gray-200 font-bold text-violet-700">{row.workato}</td>
                                        <td className="p-4 text-center text-gray-600 border-b border-gray-200">{row.mulesoft}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            </section>

            {/* CTA Section */}
            <section className="py-16 lg:py-24 relative overflow-hidden bg-slate-900">
                <div className="absolute inset-0 bg-gradient-to-br from-violet-900/40 to-slate-900 z-0"></div>
                <div className="container mx-auto px-4 lg:px-8 relative z-10">
                    <div className="max-w-4xl mx-auto text-center">
                        <h2 className="text-3xl md:text-4xl font-bold mb-6 text-white">Ready to Modernize Your Integration?</h2>
                        <p className="text-xl mb-10 text-gray-300">
                            Start your journey to Workato today. Get a free assessment of your current Mulesoft landscape.
                        </p>
                        <div className="flex flex-col sm:flex-row justify-center gap-4">
                            <Button className="h-12 px-6 text-sm font-medium rounded-md bg-violet-600 hover:bg-violet-700 group" asChild>
                                <Link href="/request-demo">
                                    <span>Contact Our Experts</span>
                                    <FaArrowRight className="ml-2 h-3.5 w-3.5 group-hover:translate-x-1 transition-transform" />
                                </Link>
                            </Button>
                            <Button variant="outline" className="h-12 px-6 text-sm font-medium rounded-md border-violet-500/40 bg-violet-500/10 text-violet-100 hover:bg-violet-500/20 hover:text-white transition-colors group" asChild>
                                <Link href="/mule-to-springboot-converter-api-analyzer">Migration Assessment</Link>
                            </Button>
                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
};

export default MulesoftToWorkato;
